import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Paper, Unstable_Grid2 as Grid} from "@mui/material";
import ConfigurableInfoBlock from "../../../../../../components/app-specific/configurable-info-block";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import Api from "../../../../../../../core/services/api/api";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const PriceLevelConfigurationsView = () => {
    const [loadingPriceLevels, setLoadingPriceLevels] = useState(true);
    const [priceLevels, setPriceLevels] = useState([]);
    const [updatingPriceLevels, setUpdatingPriceLevels] = useState([]);
    const {
        getPriceLevelDictionary,
        savePriceLevelDictionary
    } = useCachedPriceLevelsDictionaryController();
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('price-levels-configurations-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.priceLevelsConfig);
        configureBehavioralTool((tags) => tags.priceLevelsConfig);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches price levels from the server
     * @type {(function(): Promise<void>)}
     */
    const fetchPriceLevels = useCallback(async () => {
        setLoadingPriceLevels(true);
        const [allPriceLevelsResponse, priceLevelDictionary] = await Promise.all([
            Api.getAllPriceLevels(),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        if (allPriceLevelsResponse?.resultFlag) {
            allPriceLevelsResponse.data.sort((a, b) => a.levelIndex - b.levelIndex);
            setPriceLevels(allPriceLevelsResponse.data.map(priceLevel => ({
                id: priceLevel.id,
                name: priceLevel.title,
                title: priceLevelDictionary?.find(dictionaryItem => dictionaryItem.priceLevelId === priceLevel.id)?.title ?? "",
            })));
        }
        setLoadingPriceLevels(false);
    }, [isMounted, getPriceLevelDictionary])

    /**
     * As soon as the component mounts:
     * - fetches price levels from the server
     */
    useEffect(() => {
        void fetchPriceLevels();
    }, [fetchPriceLevels]);

    /**
     * Updates the title of a price level
     * @type {(function(*, *): Promise<* | undefined>)}
     */
    const updatePriceLevelTitle = useCallback(async (id, title) => {
        if (!title?.trim()) {
            return {
                resultFlag: false,
                message: 'Value is required',
            }
        }
        setUpdatingPriceLevels(prevState => [...prevState, id]);
        const response = await savePriceLevelDictionary(id, title);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPriceLevels(prevState => prevState.map(priceLevel => priceLevel.id === id ? {
                ...priceLevel,
                title
            } : priceLevel));
        }
        setUpdatingPriceLevels(prevState => prevState.filter(priceLevelId => priceLevelId !== id));
        return response;
    }, [isMounted, savePriceLevelDictionary])


    return (
        <>
            <Paper className={'d-flex flex-column p-4'}>
                <div className={'w-100 mb-4'}>
                    <p className={'text-lg font-weight-semi-bold mb-3'}>
                        Price Levels
                    </p>
                    <p className={'text-sm'}>
                        You can change the labels of the price levels in your system here.
                    </p>
                </div>
                <Grid container spacing={2}>
                    {
                        loadingPriceLevels &&
                        Array(20).fill(0).map((_, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <div className={'loading input'}>
                                    <div/>
                                </div>
                            </Grid>
                        ))
                    }
                    {
                        !loadingPriceLevels &&
                        priceLevels.map(priceLevel => (
                            <Grid item xs={12} md={6} key={priceLevel.id}>
                                <ConfigurableInfoBlock
                                    title={priceLevel.name}
                                    showHistoryIcon={false}
                                    showChartIcon={false}
                                    onValueChanged={(title) => updatePriceLevelTitle(priceLevel.id, title)}
                                    inputProps={{
                                        placeholder: priceLevel.name ?? "",
                                        value: priceLevel.title ?? '',
                                        disabled: updatingPriceLevels.includes(priceLevel.id),
                                        commitOnEnterPress: true,
                                    }}
                                />
                            </Grid>
                        ))
                    }
                    {
                        !loadingPriceLevels &&
                        !priceLevels.length &&
                        <Grid item xs={12}>
                            <p className={'text-sm'}>
                                No price levels found. Please contact your system administrator.
                            </p>
                        </Grid>
                    }
                </Grid>
            </Paper>
        </>
    );
}

export default PriceLevelConfigurationsView;
